
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen } from 'lucide-react';
import { wikiApi, type WikiDocumentDetail } from '@/shared/api';
import { splitMentions, type MentionUser } from '@/shared/lib/mentions';
import { sameOriginPath } from '@/shared/lib/safe-path';
import { onExternalLinkClick } from '@/shared/lib/external-link';
import { instructionUrlId, parseTextBlocks, toInstructionPath } from './quote-format';
import styles from './RichText.module.css';

const URL_RE = /(https?:\/\/[^\s<>()]+[^\s<>().,;:!?]|\/instructions\/[^\s<>()]+)/g;

function useInstructionTitle(href: string | null): string | null {
  const urlId = href ? instructionUrlId(href) : null;
  const { data } = useQuery({
    queryKey: ['wiki', 'document', urlId],
    queryFn: async (): Promise<WikiDocumentDetail> => (await wikiApi.document(urlId!)).data,
    enabled: Boolean(urlId),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return data?.title ?? null;
}

function InstructionChip({ href, title }: { href: string; title?: string | null }) {
  const fetched = useInstructionTitle(title ? null : href);
  return (
    <Link className={styles.chip} to={href} onClick={(e) => e.stopPropagation()}>
      <BookOpen size={13} aria-hidden />
      {title ?? fetched ?? 'Инструкция'}
    </Link>
  );
}

function Mention({ displayName, self }: { displayName: string | null; self: boolean }) {
  return (
    <span className={self ? `${styles.mention} ${styles.mentionSelf}` : styles.mention}>
      @{displayName ?? 'участник'}
    </span>
  );
}

function Linkified({ value }: { value: string }) {
  const parts = value.split(URL_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (!part) return null;
        const isUrl = /^(https?:\/\/|\/instructions\/)/.test(part);
        if (!isUrl) return <span key={i}>{part}</span>;

        const instruction = toInstructionPath(part);
        if (instruction) return <InstructionChip key={i} href={instruction} />;

        const internal = sameOriginPath(part);
        if (internal) {
          return (
            <Link key={i} className={styles.link} to={internal} onClick={(e) => e.stopPropagation()}>
              {part}
            </Link>
          );
        }

        return (
          <a
            key={i}
            className={styles.link}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onExternalLinkClick(part)}
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

export interface RichTextProps {
  text: string;
  className?: string;
  mentions?: MentionUser[];
  currentUserId?: string;
}

function Paragraph({
  value,
  mentions,
  currentUserId,
}: {
  value: string;
  mentions?: MentionUser[];
  currentUserId?: string;
}) {
  const parts = splitMentions(value, mentions);
  if (parts.length === 1 && parts[0].type === 'text') return <Linkified value={value} />;
  return (
    <>
      {parts.map((part, i) =>
        part.type === 'mention' ? (
          <Mention key={i} displayName={part.displayName} self={part.id === currentUserId} />
        ) : (
          <Linkified key={i} value={part.value} />
        ),
      )}
    </>
  );
}

export function RichText({ text, className, mentions, currentUserId }: RichTextProps) {
  const blocks = parseTextBlocks(text);

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      {blocks.map((block, i) => {
        if (block.type === 'quote') {
          const body = (
            <>
              {block.text && (
                <span className={styles.quoteText}>
                  <Linkified value={block.text} />
                </span>
              )}
              {block.source && (
                <span className={styles.quoteSource}>
                  <BookOpen size={13} aria-hidden />
                  {block.source.title}
                </span>
              )}
            </>
          );

          return block.source ? (
            <Link
              key={i}
              to={block.source.href}
              className={`${styles.quote} ${styles.quoteLink}`}
              onClick={(e) => e.stopPropagation()}
            >
              {body}
            </Link>
          ) : (
            <blockquote key={i} className={styles.quote}>
              {body}
            </blockquote>
          );
        }

        return (
          <p key={i} className={styles.paragraph}>
            <Paragraph value={block.value} mentions={mentions} currentUserId={currentUserId} />
          </p>
        );
      })}
    </div>
  );
}
